import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Default blog grid with Load more and in-place tag filtering.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The article card grid.
 * @property {HTMLElement} [actions] - The load more row.
 * @property {HTMLAnchorElement} [loadMore] - The load more control.
 *
 * @extends Component<Refs>
 */
export default class BlogStoriesList extends Component {
  /** @type {boolean} */
  #loading = false;

  /** @type {AbortController | null} */
  #filterAbort = null;

  connectedCallback() {
    super.connectedCallback();
    stripSectionIdFromAddress();
  }

  /**
   * @returns {string}
   */
  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Replaces the grid with the first page of articles for a blog or tagged URL.
   *
   * @param {string} href
   * @param {{ historyMode?: 'push' | 'replace' | 'none' }} [options]
   * @returns {Promise<void>}
   */
  async applyFilter(href, options = {}) {
    const { historyMode = 'push' } = options;
    const publicUrl = toPublicBlogUrl(href);
    publicUrl.searchParams.delete('page');

    this.#filterAbort?.abort();
    this.#filterAbort = new AbortController();
    const { signal } = this.#filterAbort;

    this.#loading = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const requestUrl = new URL(publicUrl.toString());
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, requestUrl, signal);
      if (signal.aborted) return;

      const parsedPage = new DOMParser().parseFromString(html, 'text/html');
      const incomingList = parsedPage.querySelector('blog-stories-list');
      const incomingGrid = incomingList?.querySelector('[ref="grid"]') ?? parsedPage.querySelector('[ref="grid"]');

      if (!incomingList || !incomingGrid || !this.refs.grid) return;

      this.refs.grid.replaceChildren(...incomingGrid.cloneNode(true).childNodes);
      this.dataset.nextPage = incomingList.getAttribute('data-next-page') ?? '2';
      this.dataset.lastPage = incomingList.getAttribute('data-last-page') ?? '1';
      this.#syncLoadMore(incomingList, publicUrl);

      if (historyMode === 'push') {
        history.pushState({ blogFilter: true }, '', publicPath(publicUrl));
      } else if (historyMode === 'replace') {
        history.replaceState({ blogFilter: true }, '', publicPath(publicUrl));
      }
    } catch (error) {
      if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
      throw error;
    } finally {
      if (!signal.aborted) {
        this.#loading = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Fetches the next paginated page and appends its cards to the grid.
   *
   * @param {Event} [event]
   * @returns {Promise<void>}
   */
  async loadMore(event) {
    event?.preventDefault();
    if (this.#loading) return;

    const { grid, loadMore } = this.refs;
    const nextPage = Number(this.dataset.nextPage);
    const lastPage = Number(this.dataset.lastPage);

    if (!grid || !nextPage || !lastPage || nextPage > lastPage) return;

    this.#loading = true;
    if (loadMore) {
      loadMore.setAttribute('aria-disabled', 'true');
      loadMore.setAttribute('aria-busy', 'true');
    }

    try {
      const requestUrl = toPublicBlogUrl(window.location.href);
      requestUrl.searchParams.set('page', String(nextPage));

      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(requestUrl.toString()));
      const parsedPage = new DOMParser().parseFromString(html, 'text/html');
      const pageGrid = parsedPage.querySelector('[ref="grid"]');
      const cards = pageGrid?.querySelectorAll(':scope > [ref="cards[]"]');

      if (cards?.length) {
        grid.append(...cards);
      }

      const followingPage = nextPage + 1;
      this.dataset.nextPage = String(followingPage);

      if (loadMore) {
        if (followingPage > lastPage) {
          loadMore.hidden = true;
          this.refs.actions?.classList.add('is-hidden');
        } else {
          const followingUrl = toPublicBlogUrl(window.location.href);
          followingUrl.searchParams.set('page', String(followingPage));
          loadMore.href = publicPath(followingUrl);
        }
      }
    } finally {
      this.#loading = false;
      if (loadMore && !loadMore.hidden) {
        loadMore.removeAttribute('aria-disabled');
        loadMore.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {Element} incomingList
   * @param {URL} [publicUrl]
   */
  #syncLoadMore(incomingList, publicUrl) {
    const { loadMore } = this.refs;
    if (!loadMore) return;

    const lastPage = Number(this.dataset.lastPage);
    const nextPage = Number(this.dataset.nextPage);
    const hasMore = lastPage > 1 && nextPage <= lastPage;
    const { actions } = this.refs;

    /** @type {HTMLAnchorElement | null} */
    const incomingLoadMore = incomingList.querySelector('[ref="loadMore"]');

    if (hasMore) {
      const nextHref = incomingLoadMore?.getAttribute('href');
      const nextUrl = nextHref ? toPublicBlogUrl(nextHref) : publicUrl ? new URL(publicUrl.toString()) : null;
      if (nextUrl && !nextUrl.searchParams.has('page')) {
        nextUrl.searchParams.set('page', String(nextPage));
      }

      loadMore.hidden = false;
      loadMore.href = nextUrl ? publicPath(nextUrl) : publicPath(toPublicBlogUrl(window.location.href));
      loadMore.removeAttribute('aria-disabled');
      loadMore.removeAttribute('aria-busy');
      actions?.classList.remove('is-hidden');
    } else {
      loadMore.hidden = true;
      actions?.classList.add('is-hidden');
    }
  }
}

/**
 * Tag filter pills that refresh the blog grid without a full page reload.
 *
 * @extends Component
 */
class BlogStoryFiltersNav extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
    stripSectionIdFromAddress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const eventTarget = event.target;
    const fromElement =
      eventTarget instanceof Element
        ? eventTarget
        : eventTarget instanceof Node
          ? eventTarget.parentElement
          : null;
    const link = fromElement?.closest('a.blog-story-filters__link');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    if (link.classList.contains('is-active')) return;

    this.#setActive(link);

    const list = document.querySelector('blog-stories-list');
    if (!(list instanceof BlogStoriesList)) {
      window.location.assign(link.href);
      return;
    }

    list.applyFilter(link.href);
  };

  #onPopState = () => {
    this.#setActiveFromUrl(window.location.href);

    const list = document.querySelector('blog-stories-list');
    if (list instanceof BlogStoriesList) {
      list.applyFilter(window.location.href, { historyMode: 'none' });
    }
  };

  /**
   * @param {HTMLAnchorElement} activeLink
   */
  #setActive(activeLink) {
    for (const link of this.querySelectorAll('a.blog-story-filters__link')) {
      const isActive = link === activeLink;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {string} href
   */
  #setActiveFromUrl(href) {
    const currentPath = normalizeBlogPath(href);

    for (const link of this.querySelectorAll('a.blog-story-filters__link')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const isActive = normalizeBlogPath(link.href) === currentPath;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

/**
 * @param {string} href
 * @returns {URL}
 */
function toPublicBlogUrl(href) {
  const url = new URL(href, window.location.origin);
  url.searchParams.delete('section_id');
  url.hash = '';
  return url;
}

/**
 * @param {URL} url
 * @returns {string}
 */
function publicPath(url) {
  return `${url.pathname}${url.search}`;
}

/**
 * @param {string} href
 * @returns {string}
 */
function normalizeBlogPath(href) {
  return toPublicBlogUrl(href).pathname.replace(/\/$/, '');
}

function stripSectionIdFromAddress() {
  const url = toPublicBlogUrl(window.location.href);
  if (window.location.search.includes('section_id')) {
    history.replaceState(history.state, '', publicPath(url));
  }
}

if (!customElements.get('blog-stories-list')) {
  customElements.define('blog-stories-list', BlogStoriesList);
}

if (!customElements.get('blog-story-filters-nav')) {
  customElements.define('blog-story-filters-nav', BlogStoryFiltersNav);
}
